
import { PuttingResult } from "@/types/puttingResults";
import { supabase } from "@/integrations/supabase/client";

/**
 * Fetches all putting training results for a specific user from both the 'level_tests_results' and 'putting_training_results' tables.
 * Combines and sorts the results by test_date in descending order.
 */
export const fetchAllPuttingTrainingResults = async (userId: string): Promise<any[]> => {
  try {
    console.log('fetchAllPuttingTrainingResults called with userId:', userId);
    
    // Fetch results from 'level_tests_results' table
    const { data: levelResults, error: levelError } = await supabase
      .from('level_tests_results')
      .select('*')
      .eq('user_id', userId)
      .order('test_date', { ascending: false });

    console.log('Level results query:', { data: levelResults?.length || 0, error: levelError });

    if (levelError) {
      console.error('Error fetching level_tests_results:', levelError);
      return [];
    }

    // Fetch results from 'putting_test_results' table
    const { data: puttingResults, error: puttingError } = await supabase
      .from('putting_test_results')
      .select('*')
      .eq('user_id', userId)
      .order('test_date', { ascending: false });

    if (puttingError) {
      console.error('Error fetching putting_test_results:', puttingError);
      return [];
    }

    // Combine results from both tables (individual_scores are already saved to level_tests_results when finishing a test)
    const allResults = [...(levelResults || []), ...(puttingResults || [])];
    
    // Sort combined results by test_date in descending order
    allResults.sort((a: any, b: any) => {
      const dateA = a.test_date ? new Date(a.test_date).getTime() : 0;
      const dateB = b.test_date ? new Date(b.test_date).getTime() : 0;
      return dateB - dateA;
    });

    return allResults;
  } catch (error) {
    console.error('Error fetching all putting training results:', error);
    return [];
  }
};

/**
 * Fetches putting test results for a specific user (standard Pelz tests without levels)
 */
export const fetchPuttingTestResults = async (userId: string): Promise<any[]> => {
  try {
    // Fetch results from 'putting_test_results' table only (no level tests)
    const { data: puttingResults, error: puttingError } = await supabase
      .from('putting_test_results')
      .select('*')
      .eq('user_id', userId)
      .order('test_date', { ascending: false });

    if (puttingError) {
      console.error('Error fetching putting_test_results:', puttingError);
      return [];
    }
    
    return puttingResults || [];
  } catch (error) {
    console.error('Error fetching putting test results:', error);
    return [];
  }
};

/**
 * Fetch swing tendencies for a user
 */
export const fetchSwingTendencies = async (userId: string): Promise<any[]> => {
  try {
    console.log("Fetching swing tendencies for user:", userId);
    
    const { data, error } = await supabase
      .from('swing_tendencies')
      .select('*')
      .eq('user_id', userId)
      .order('test_date', { ascending: false });
      
    if (error) {
      console.error('Error fetching swing tendencies:', error);
      return [];
    }
    
    console.log("Fetched swing tendencies:", data?.length || 0);
    
    // Make sure all tendencies have a test_level defined
    const processedData = (data || []).map(tendency => {
      if (!tendency.test_level || tendency.test_level.trim() === '') {
        // Try to extract level from test_id if available
        if (tendency.test_id && tendency.test_id.includes('-l')) {
          const match = tendency.test_id.match(/-l(\d+)/i);
          if (match && match[1]) {
            tendency.test_level = match[1];
          } else {
            tendency.test_level = '3'; // Default to level 3 for swing
          }
        } else {
          tendency.test_level = '3'; // Default to level 3 for swing
        }
      }
      return tendency;
    });
    
    return processedData;
  } catch (err) {
    console.error('Exception fetching swing tendencies:', err);
    return [];
  }
};

/**
 * Fetch driver test results from level_tests_results table
 * This will get the driver-specific test results that include level information
 */
export const fetchDriverTestResults = async (userId: string): Promise<any[]> => {
  try {
    console.log("Fetching driver test results for user:", userId);
    
    // Get driver entries from level_tests_results
    const { data: levelData, error: levelError } = await supabase
      .from('level_tests_results')
      .select('*')
      .eq('user_id', userId)
      .eq('test_id', 'driver-precision')
      .order('test_date', { ascending: false });
      
    if (levelError) {
      console.error('Error fetching driver test results from level_tests_results:', levelError);
      return [];
    }
    
    // Make sure all level data has test_level defined by mapping level to test_level
    const processedLevelData = (levelData || []).map(result => {
      // Create a new object with all properties from result
      // and add test_level based on the existing level property
      return {
        ...result,
        test_level: result.level || '3' // Use level field or default to 3
      };
    });
    
    // Also get any driver entries from putting_test_results that might be there incorrectly
    const { data: puttingData, error: puttingError } = await supabase
      .from('putting_test_results')
      .select('*')
      .eq('user_id', userId)
      .order('test_date', { ascending: false });
      
    if (puttingError) {
      console.error('Error fetching from putting_test_results:', puttingError);
      return processedLevelData;
    }
    
    // Filter putting results to only include driver-related entries (if any)
    const driverPuttingData = (puttingData || []).filter((result: any) => {
      // Check if the test_name or metadata indicates it's a driver test
      const isDriverTest = 
        // Check test_name property exists and is a string containing "driver"
        (result && 'test_name' in result && 
         typeof result.test_name === 'string' && 
         result.test_name.toLowerCase().includes('driver')) ||
        // Check test_id property exists and is a string containing "driver"
        (result && 'test_id' in result && 
         typeof result.test_id === 'string' && 
         result.test_id.toLowerCase().includes('driver'));
      
      return isDriverTest;
    });
    
    // Add test_level to putting data if missing
    const processedPuttingData = driverPuttingData.map((result: any) => {
      if (!result.test_level || result.test_level.trim() === '') {
        result.test_level = '3'; // Default to level 3 for driver tests from putting_test_results
      }
      return result;
    });
    
    console.log('Fetched driver test results from level_tests_results:', processedLevelData.length);
    console.log('Fetched driver test results from putting_test_results:', processedPuttingData.length);
    
    // Combine results from both tables
    return [...processedLevelData, ...processedPuttingData];
  } catch (err) {
    console.error('Exception fetching driver test results:', err);
    return [];
  }
};
